'use client'

import { createContext, useContext, useEffect, useState } from 'react'

type ThemePreference = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  theme: ThemePreference
  resolvedTheme: ResolvedTheme
  setTheme: (theme: ThemePreference) => void
  toggleTheme: () => void
}

const THEME_STORAGE_KEY = 'theme-preference'

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function applyThemeToDom(theme: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme] = useState<ThemePreference>('light')
  const [resolvedTheme] = useState<ResolvedTheme>('light')

  useEffect(() => {
    // Force light theme and clear any previously stored dark preference
    localStorage.setItem(THEME_STORAGE_KEY, 'light')
    applyThemeToDom('light')
  }, [])

  const setTheme = (_nextTheme: ThemePreference) => {
    // Dark mode disabled — always light
  }

  const toggleTheme = () => {
    // Dark mode disabled
  }

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
